import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import AssessmentIcon from '@mui/icons-material/Assessment'
import CloseIcon from '@mui/icons-material/Close'
import CodeIcon from '@mui/icons-material/Code'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import LogoutIcon from '@mui/icons-material/Logout'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { useTranslation } from 'react-i18next'
import LogoAsr from '../assets/logo_voxist_asr.svg'
import { tokens } from '../theme/tokens'

interface MobileDrawerProps {
  open: boolean
  onClose: () => void
  user: any
  isAdmin: boolean
  onNavigate: (path: string) => void
  onBillingOpen: () => void
  onUsageOpen: () => void
  onApiKeysOpen: () => void
  onSignOut: () => void
  currentPath: string
}

const DRAWER_WIDTH = 280

const MobileDrawer = ({
  open,
  onClose,
  isAdmin,
  onNavigate,
  onBillingOpen,
  onUsageOpen,
  onApiKeysOpen,
  onSignOut,
  currentPath,
}: MobileDrawerProps) => {
  const { t } = useTranslation()

  const isActive = (prefix: string) => currentPath.startsWith(prefix)

  const activeStyles = {
    backgroundColor: `${tokens.colors.primary.darkText}14`,
    borderRadius: tokens.radius.sm,
  }

  const handleNav = (path: string) => {
    onNavigate(path)
    onClose()
  }

  const handleBilling = () => {
    onBillingOpen()
    onClose()
  }

  const handleUsage = () => {
    onUsageOpen()
    onClose()
  }

  const handleApiKeys = () => {
    onApiKeysOpen()
    onClose()
  }

  const handleSignOut = () => {
    onSignOut()
    onClose()
  }

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
      aria-label="Navigation menu"
      ModalProps={{ keepMounted: true }}
      PaperProps={{ sx: { width: DRAWER_WIDTH } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
        }}
      >
        <img src={LogoAsr} alt="Voxist ASR" style={{ height: 40 }} />
        <IconButton onClick={onClose} aria-label="Close navigation" size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* Primary navigation */}
      <List component="nav" aria-label={t('nav.mobileNav', 'Mobile navigation')} sx={{ px: 1, py: 1 }}>
        <ListItem
          button
          onClick={() => handleNav('/speech')}
          aria-current={isActive('/speech') ? 'page' : undefined}
          sx={isActive('/speech') ? activeStyles : undefined}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <RecordVoiceOverIcon
              aria-hidden="true"
              sx={{ color: isActive('/speech') ? tokens.colors.primary.darkText : undefined }}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('nav.speech', 'Speech')}
            primaryTypographyProps={{
              fontWeight: isActive('/speech') ? 600 : 400,
              color: isActive('/speech') ? tokens.colors.primary.darkText : undefined,
            }}
          />
        </ListItem>

        <ListItem
          button
          onClick={() => handleNav('/voices')}
          aria-current={isActive('/voices') ? 'page' : undefined}
          sx={isActive('/voices') ? activeStyles : undefined}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <GraphicEqIcon
              aria-hidden="true"
              sx={{ color: isActive('/voices') ? tokens.colors.primary.darkText : undefined }}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('nav.voices', 'Voices')}
            primaryTypographyProps={{
              fontWeight: isActive('/voices') ? 600 : 400,
              color: isActive('/voices') ? tokens.colors.primary.darkText : undefined,
            }}
          />
        </ListItem>

        <ListItem
          button
          onClick={() => handleNav('/docs')}
          aria-current={isActive('/docs') ? 'page' : undefined}
          sx={isActive('/docs') ? activeStyles : undefined}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <CodeIcon
              aria-hidden="true"
              sx={{ color: isActive('/docs') ? tokens.colors.primary.darkText : undefined }}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('nav.docs', 'Docs')}
            primaryTypographyProps={{
              fontWeight: isActive('/docs') ? 600 : 400,
              color: isActive('/docs') ? tokens.colors.primary.darkText : undefined,
            }}
          />
        </ListItem>
      </List>

      <Divider />

      {/* Account actions */}
      <List sx={{ px: 1, py: 1 }}>
        <ListItem button onClick={handleBilling}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <AccountBalanceWalletIcon />
          </ListItemIcon>
          <ListItemText primary={t('billing', 'Billing')} />
        </ListItem>

        <ListItem button onClick={handleUsage}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <AssessmentIcon />
          </ListItemIcon>
          <ListItemText primary={t('usage', 'Usage')} />
        </ListItem>

        <ListItem button onClick={handleApiKeys}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <VpnKeyIcon />
          </ListItemIcon>
          <ListItemText primary={t('apiKeys', 'API Keys')} />
        </ListItem>
      </List>

      {/* Admin section */}
      {isAdmin && (
        <>
          <Divider />
          <List sx={{ px: 1, py: 1 }}>
            <ListItem
              button
              onClick={() => handleNav('/admin')}
              sx={isActive('/admin') ? activeStyles : undefined}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AdminPanelSettingsIcon
                  sx={{ color: isActive('/admin') ? tokens.colors.primary.darkText : undefined }}
                />
              </ListItemIcon>
              <ListItemText
                primary={t('admin', 'Admin')}
                primaryTypographyProps={{
                  fontWeight: isActive('/admin') ? 600 : 400,
                  color: isActive('/admin') ? tokens.colors.primary.darkText : undefined,
                }}
              />
            </ListItem>
          </List>
        </>
      )}

      <Divider />

      {/* Sign out */}
      <List sx={{ px: 1, py: 1 }}>
        <ListItem button onClick={handleSignOut}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary={t('signOut', 'Sign Out')} />
        </ListItem>
      </List>
    </Drawer>
  )
}

export default MobileDrawer
